/* ══════════════════════════════════════════════════════════════════════
   BRUTECH HEADER  ·  build 13
   Written from scratch.

   Everything is driven by data attributes on .bh-shell, so nothing is
   hardcoded and every value stays editable from the theme editor.

   Two decisions are worth stating up front, because both were arrived at
   the expensive way:

   1. Menu open state uses mouseenter and mouseleave on the <li>. Those two
      events are evaluated against the element's DOM subtree, not against
      its box. The panel is a child of the <li>, so moving from the tab down
      into a panel that spans the page well below the header never fires a
      leave. Every earlier version used pointer coordinates, a safe corridor
      or a bridge element, and every one of them dropped the menu somewhere.
      There is no geometry in this file.

   2. The header rises out of its stacking context only while a panel is
      open, and drops back when it closes. Shopify renders this section
      inside a header section group, and that wrapper opens its own stacking
      context, so a z-index on the section cannot beat a section further
      down the page. The panel then paints under that section, and painting
      order is hit-testing order, so it looks perfect and swallows every
      click. Lifting only while open keeps the header out of the way of the
      theme's own drawers the rest of the time.
   ══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var VERSION = '2026-08-04-rebuild-v21';

  /* Pick the shell that is actually rendered. A duplicated header in the
     theme editor, or one hidden behind [hidden], must not win. */
  var shells = [].slice.call(document.querySelectorAll('.bh-shell[data-bh]'));
  var shell = shells.filter(function (el) {
    return !el.closest('[hidden]') && el.getClientRects().length > 0;
  })[0] || shells[0];
  if (!shell) return;

  var root = shell.closest('.bh-root') || shell.parentElement;
  var cfg = shell.dataset;
  var reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  shell.dataset.bhVersion = VERSION;

  function pad(n) { return ('0' + n).slice(-2); }
  function $(sel, ctx) { return (ctx || shell).querySelector(sel); }
  function $$(sel, ctx) { return [].slice.call((ctx || shell).querySelectorAll(sel)); }

  /* ══════════════════════════════════════════════════════════════════
     1. RARITY TINT
     The bar takes the colour of the page being viewed. "uncommon" contains
     "common", so the list is walked from the rare end down and the first
     hit wins.
     ══════════════════════════════════════════════════════════════════ */
  (function tint() {
    var tiers = (cfg.tiers || '').split(',').filter(Boolean);
    var hex = (cfg.tierHex || '').split(',').filter(Boolean);
    if (!tiers.length) return;
    var path = location.pathname.toLowerCase();
    for (var i = tiers.length - 1; i >= 0; i--) {
      if (path.indexOf(tiers[i]) === -1) continue;
      shell.style.setProperty('--bh-brand', hex[i]);
      shell.style.setProperty('--bh-brand-rgb', toRgb(hex[i]));
      document.body.dataset.rarity = tiers[i];
      return;
    }
  })();
  function toRgb(h) {
    var n = parseInt(String(h).replace('#', ''), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255].join(',');
  }

  /* ══════════════════════════════════════════════════════════════════
     2. STACKING LIFT
     Raise every wrapper between the section and <body> above anything else
     on the page, and put them all back afterwards. The target value is
     measured rather than picked, so it clears whatever the theme is
     actually using instead of relying on a number being big enough.
     ══════════════════════════════════════════════════════════════════ */
  var lifted = [];
  var liftedActive = false;
  var rootZ0 = null;
  var suggestOpen = false;
  var overlayOpen = 0;

  var forcedZ = 0;

  /* Measuring the page can only ever be a starting guess. A sticky step bar
     nested inside a section that has no z-index of its own still competes in
     the root stacking context, and no reasonable scan finds every one of
     those. So the measurement is the opening bid and verifyOnTop below is
     the actual guarantee. */
  function highestZOutside() {
    if (forcedZ) return forcedZ;
    var top = 100;
    var els = document.querySelectorAll(
      'body > *, [id^="shopify-section"], .shopify-section, [style*="z-index"], ' +
      '[class*="sticky"], [class*="Sticky"], [class*="fixed"], header, nav'
    );
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el === root || el.contains(root) || root.contains(el)) continue;
      var z = parseInt(getComputedStyle(el).zIndex, 10);
      if (!isNaN(z) && z >= top && z < 2147483000) top = z + 10;
    }
    return top + 10;
  }

  function applyZ(z) {
    /* Straight onto .bh-root, not onto a custom property on the shell.
       Custom properties inherit downward only, and .bh-root is the shell's
       parent, so a --bh-z set on the shell is invisible to the rule that
       reads it and .bh-root sat at its 100 fallback forever. The lift only
       ever appeared to work because a section group wrapper above it was
       being raised as well, and a theme that renders this section as a
       direct child of <body> has no such wrapper. */
    root.style.zIndex = String(z);
    shell.style.setProperty('--bh-z', String(z));
    for (var i = 0; i < lifted.length; i++) {
      var nz = parseInt(getComputedStyle(lifted[i].el).zIndex, 10);
      if (isNaN(nz) || nz < z) lifted[i].el.style.zIndex = String(z);
    }
  }

  function liftUp() {
    /* Guarded on its own flag, not on lifted.length. With no wrapper between
       the section and <body> there is nothing to record, and length stayed
       zero, which silently disabled the verification below. */
    if (liftedActive || !root) return;
    liftedActive = true;
    rootZ0 = root.style.zIndex;
    var node = root.parentElement;
    while (node && node.nodeType === 1 && node !== document.body && node !== document.documentElement) {
      var cs = getComputedStyle(node);
      /* Remember exactly what was there, including "nothing", so the
         restore is a true restore and not a guess. */
      lifted.push({
        el: node,
        position: node.style.position,
        zIndex: node.style.zIndex,
        overflowX: node.style.overflowX,
        overflowY: node.style.overflowY
      });
      if (cs.position === 'static') node.style.position = 'relative';
      /* A wrapper that clips would cut the panel off at the header edge. */
      if (cs.overflowX === 'hidden' || cs.overflowX === 'clip') node.style.overflowX = 'visible';
      if (cs.overflowY === 'hidden' || cs.overflowY === 'clip') node.style.overflowY = 'visible';
      node = node.parentElement;
    }
    applyZ(highestZOutside());
    document.documentElement.dataset.bhMenuOpen = 'true';
  }

  function dropBack() {
    if (!liftedActive) return;
    liftedActive = false;
    if (root) root.style.zIndex = rootZ0 || '';
    for (var i = 0; i < lifted.length; i++) {
      var r = lifted[i];
      r.el.style.position = r.position;
      r.el.style.zIndex = r.zIndex;
      r.el.style.overflowX = r.overflowX;
      r.el.style.overflowY = r.overflowY;
    }
    lifted = [];
    shell.style.removeProperty('--bh-z');
    delete document.documentElement.dataset.bhMenuOpen;
  }

  /* The guarantee. Once a panel is actually painted, ask the browser which
     element is on top at a point inside it. If the answer is not the panel,
     something on this page outranks the measurement, so go straight to a
     value nothing sensible will beat. It is safe to go that high because the
     lift is released the moment the panel closes, so the header is not
     parked above the theme's own drawers and modals the rest of the time.

     This runs on a rAF, after paint, so it tests what the user is looking at
     rather than what the stylesheet claims. */
  function verifyOnTop(panel) {
    if (!panel || !document.elementFromPoint) return;
    requestAnimationFrame(function () {
      if (!liftedActive) return;
      var r = panel.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return;
      var x = Math.round(r.left + Math.min(r.width / 2, 240));
      var y = Math.round(Math.min(r.top + Math.min(r.height / 2, 40), innerHeight - 2));
      if (y < 0 || x < 0 || x > innerWidth) return;
      var hit = document.elementFromPoint(x, y);
      if (hit && panel.contains(hit)) return;
      forcedZ = 2147482000;
      applyZ(forcedZ);
    });
  }

  /* ══════════════════════════════════════════════════════════════════
     3. MEGA MENUS
     ══════════════════════════════════════════════════════════════════ */
  var items = $$('[data-bh-menu]');
  var CLOSE_DELAY = Math.max(parseInt(cfg.delay, 10) || 260, 220);
  var activeItem = null;
  var closeTimer = null;

  function triggerOf(item) { return item.querySelector('.bh-nav-link'); }
  function panelOf(item) { return item.querySelector('[data-bh-panel]'); }

  function cancelClose() {
    if (closeTimer) clearTimeout(closeTimer);
    closeTimer = null;
  }

  function setOpen(item, open) {
    if (!item) return;
    item.dataset.open = open ? 'true' : 'false';
    var t = triggerOf(item);
    if (t) t.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      activeItem = item;
    } else if (activeItem === item) {
      activeItem = null;
    }
    updateLift();
  }

  function anyOpen() {
    for (var i = 0; i < items.length; i++) if (items[i].dataset.open === 'true') return true;
    return false;
  }

  /* The suggestion panel hangs below the header exactly like a mega panel
     does, so it needs the same lift, and neither may drop it while the
     other is still open. */
  function updateLift() {
    if (anyOpen() || suggestOpen || overlayOpen) liftUp();
    else dropBack();
  }

  /* The cart panel and the mobile drawer are fixed overlays inside this
     section, so a page section with a higher z-index paints over them just
     as readily as it did over a mega panel. They go through the same lift,
     and the same after-paint hit test proves it worked. */
  function setOverlay(on, panel) {
    overlayOpen = Math.max(0, overlayOpen + (on ? 1 : -1));
    /* The page behind an open drawer cannot take focus or be read past. */
    var mainEl = document.getElementById('main');
    if (mainEl) {
      if (overlayOpen > 0) mainEl.setAttribute('inert', ''); else mainEl.removeAttribute('inert');
    }
    updateLift();
    if (on) verifyOnTop(panel);
  }

  function openItem(item) {
    if (!item) return;
    cancelClose();
    updateLift();
    for (var i = 0; i < items.length; i++) {
      if (items[i] !== item) items[i].dataset.open = 'false';
      if (items[i] !== item) {
        var t = triggerOf(items[i]);
        if (t) t.setAttribute('aria-expanded', 'false');
      }
    }
    setOpen(item, true);
    verifyOnTop(panelOf(item));
  }

  function closeAll() {
    cancelClose();
    for (var i = 0; i < items.length; i++) setOpen(items[i], false);
  }

  /* Give the lift back the moment a navigation starts.
     Closing a panel releases it, but following a link out of one does not:
     the page stays put while the theme paints its loading overlay, and the
     header is still raised above everything at that point, so it punches
     straight through the overlay. Every path that leaves the page routes
     through here first. */
  function releaseLift() {
    cancelClose();
    for (var i = 0; i < items.length; i++) {
      items[i].dataset.open = 'false';
      var t = triggerOf(items[i]);
      if (t) t.setAttribute('aria-expanded', 'false');
    }
    activeItem = null;
    suggestOpen = false;
    var panels = document.querySelectorAll('[data-bh-suggest-panel]');
    for (var j = 0; j < panels.length; j++) panels[j].hidden = true;
    /* An overlay still on screen keeps the lift. Dropping it here would put
       the cart panel under the page for as long as it stays open. */
    if (!overlayOpen) dropBack();
  }

  /* Any ordinary left click on a link that is going somewhere.
     Deliberately does not check defaultPrevented. A theme with page
     transitions cancels the click and navigates itself, so defaultPrevented
     is true on exactly the journeys that matter. If the click really was
     cancelled and nothing happens, all this did was close the menu, which
     is the right thing anyway. */
  document.addEventListener('click', function (e) {
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target.closest && e.target.closest('a[href]');
    if (!a || a.target === '_blank') return;
    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#' || href.indexOf('javascript:') === 0) return;
    releaseLift();
  });

  /* Belt: a navigation started some other way, or a bfcache restore. */
  addEventListener('pagehide', releaseLift);

  function scheduleClose(item, delay) {
    cancelClose();
    closeTimer = setTimeout(function () {
      closeTimer = null;
      /* Never close out from under a keyboard user. */
      if (item.contains(document.activeElement)) return;
      setOpen(item, false);
    }, typeof delay === 'number' ? delay : CLOSE_DELAY);
  }

  items.forEach(function (item) {
    var trigger = triggerOf(item);
    var panel = panelOf(item);
    if (!trigger || !panel) return;

    item.dataset.open = 'false';
    trigger.setAttribute('aria-expanded', 'false');

    /* Subtree based, not box based. See the note at the top of the file. */
    item.addEventListener('mouseenter', function () {
      if (item._touch) return;
      delete item.dataset.esc;
      openItem(item);
    });
    item.addEventListener('mouseleave', function () {
      delete item.dataset.esc;
      scheduleClose(item);
    });

    item.addEventListener('focusin', function () {
      delete item.dataset.esc;
      openItem(item);
    });
    item.addEventListener('focusout', function () {
      setTimeout(function () {
        if (!item.contains(document.activeElement)) scheduleClose(item, 100);
      }, 0);
    });

    trigger.addEventListener('pointerdown', function (e) {
      item._touch = (e.pointerType === 'touch' || e.pointerType === 'pen');
      item._pointer = e.pointerType || 'mouse';
    });

    trigger.addEventListener('click', function (e) {
      var href = (trigger.getAttribute('href') || '').trim();
      var navigable = !!href && href !== '#' && href.indexOf('javascript:') !== 0;
      var touchLike = item._touch;
      var keyboard = e.detail === 0;

      /* A mouse click on a tab that is also a link just follows it. */
      if (!touchLike && !keyboard && navigable) {
        item._pointer = '';
        return;
      }

      e.preventDefault();

      if (touchLike) {
        /* First tap opens, second tap follows the link. */
        if (item.dataset.open === 'true' && navigable) {
          releaseLift();
          location.href = href;
          return;
        }
        openItem(item);
      } else if (item.dataset.open === 'true') {
        setOpen(item, false);
      } else {
        openItem(item);
      }
      item._pointer = '';
    });
  });

  /* Outside pointerdown closes. This reads the whole hit stack rather than
     e.target alone, so something painting over the panel can never be
     mistaken for the user clicking away from the menu. */
  document.addEventListener('pointerdown', function (e) {
    if (!activeItem) return;
    if (activeItem.contains(e.target)) return;
    if (document.elementsFromPoint) {
      var stack = document.elementsFromPoint(e.clientX, e.clientY);
      for (var i = 0; i < stack.length; i++) {
        if (activeItem.contains(stack[i])) return;
      }
    }
    closeAll();
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var focused = document.activeElement;
    items.forEach(function (item) {
      /* data-esc holds the panel shut against the CSS :hover fallback until
         the pointer leaves, otherwise Escape does nothing while hovering. */
      if (item.dataset.open === 'true') item.dataset.esc = 'true';
      setOpen(item, false);
      if (item.contains(focused)) {
        var t = triggerOf(item);
        if (t) t.focus();
      }
    });
  });

  /* Last line of defence. If anything ever paints over an open panel again,
     the click lands on that thing instead of the card and the menu goes
     dead with no visible symptom, which is a genuinely hard bug to spot.
     This walks the full hit stack and follows the card link it finds. On a
     working header it never does anything, because the click already
     reached a link and this handler returns immediately. */
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (e.target.closest && e.target.closest('a[href], button, input, select, textarea, label')) return;
    if (!document.elementsFromPoint) return;
    var stack = document.elementsFromPoint(e.clientX, e.clientY);
    for (var i = 0; i < stack.length; i++) {
      var link = stack[i].closest && stack[i].closest('[data-bh-panel] a[href]');
      if (!link) continue;
      var panel = link.closest('[data-bh-panel]');
      if (!panel || getComputedStyle(panel).visibility !== 'visible') continue;
      e.preventDefault();
      releaseLift();
      location.href = link.href;
      return;
    }
  }, true);

  /* Category colour on the panel glow, for menus with no tint saved. */
  var CATEGORY = { workstations: '#a855f7', accessories: '#f59e0b', laptops: '#38bdf8' };
  items.forEach(function (item) {
    if (item.style.getPropertyValue('--tint')) return;
    var text = (item.textContent || '').toLowerCase();
    var key = /workstation|creator pc/.test(text) ? 'workstations'
            : /laptop|notebook/.test(text) ? 'laptops'
            : /accessor|peripheral|keyboard|mouse|headset|mousepad|microphone|webcam/.test(text) ? 'accessories'
            : '';
    if (key) {
      item.dataset.bhCategory = key;
      item.style.setProperty('--tint', CATEGORY[key]);
    }
  });

  /* ══════════════════════════════════════════════════════════════════
     4. CONDENSE AND TUCK
     rAF throttled, transform only, so it stays on the compositor.
     ══════════════════════════════════════════════════════════════════ */
  (function scrollBehaviour() {
    var lastY = 0, ticking = false;
    function apply() {
      var y = window.scrollY;
      shell.dataset.small = String(y > 80);
      if (cfg.tuck === 'true') {
        if (!anyOpen() && y > 320 && y > lastY + 6) shell.dataset.tucked = 'true';
        else if (y < lastY - 6 || y <= 320) shell.dataset.tucked = 'false';
      }
      lastY = y;
    }
    addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { apply(); ticking = false; });
    }, { passive: true });
    apply();
  })();

  document.addEventListener('visibilitychange', function () {
    document.documentElement.dataset.pageHidden = String(document.hidden);
  });

  /* ══════════════════════════════════════════════════════════════════
     5. INVENTORY
     Free delivery covers builds, not accessories bought on their own, so
     the mark is gated on the cart actually holding a build. Claiming it
     otherwise is a promise checkout would break.
     ══════════════════════════════════════════════════════════════════ */
  (function inventory() {
    var inv = $('[data-bh-inv]');
    if (!inv) return;
    var SLOTS = 5;
    var buildTypes = (cfg.buildTypes || '').split(',')
      .map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean);
    var lastCount;

    function money(cents) {
      try {
        return new Intl.NumberFormat('en-ZA', {
          style: 'currency', currency: cfg.currency || 'ZAR', maximumFractionDigits: 0
        }).format(cents / 100);
      } catch (e) {
        return 'R' + Math.round(cents / 100);
      }
    }
    function put(sel, val) { var el = inv.querySelector(sel); if (el) el.textContent = val; }

    function paint(cart) {
      if (!cart) return;
      var count = cart.item_count || 0;
      var total = cart.total_price || 0;
      var hasBuild = (cart.items || []).some(function (line) {
        var type = (line.product_type || '').toLowerCase();
        return buildTypes.some(function (b) { return b && type.indexOf(b) !== -1; });
      });

      put('[data-inv-n]', count);
      put('[data-inv-total]', money(total));

      var badge = inv.querySelector('[data-inv-count]');
      if (badge) { badge.textContent = count; badge.dataset.empty = String(count === 0); }

      var free = inv.querySelector('[data-inv-free]');
      if (free) {
        free.dataset.plain = String(!hasBuild);
        free.textContent = count === 0 ? 'Empty' : hasBuild ? 'Free delivery' : 'Delivery at checkout';
      }

      var slots = inv.querySelector('[data-inv-slots]');
      if (slots) {
        var html = '';
        for (var i = 0; i < SLOTS; i++) html += '<span class="bh-slot" data-on="' + (i < count) + '"></span>';
        slots.innerHTML = html;
      }

      inv.setAttribute('aria-label',
        'Cart, ' + count + ' item' + (count === 1 ? '' : 's') + ', ' + money(total) +
        (hasBuild ? ', free delivery included' : ''));

      if (lastCount !== undefined && count !== lastCount) {
        inv.dataset.bump = 'true';
        setTimeout(function () { delete inv.dataset.bump; }, 420);
      }
      lastCount = count;
    }

    function refresh() {
      var base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      fetch(base + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(paint)
        .catch(function () {});
    }

    refresh();
    ['cart:updated', 'cart:refresh', 'cart:change', 'variant:added', 'cart:added']
      .forEach(function (ev) { document.addEventListener(ev, refresh); });
  })();

  /* ══════════════════════════════════════════════════════════════════
     6. COUNTDOWNS
     Under a day the drop timer reads HH:MM:SS, which lands very
     differently to "0d".
     ══════════════════════════════════════════════════════════════════ */
  (function countdowns() {
    var closeDay = parseInt(cfg.closeDay, 10) || 3;
    var qteEnds = cfg.qteEnds ? new Date(cfg.qteEnds) : null;
    var dropEls = document.querySelectorAll('[data-drop-timer]');
    var qteEls = document.querySelectorAll('[data-qte-timer]');
    if (!dropEls.length && !qteEls.length) return;

    /* The server decides when the drop closes and what time it is now (shop time), so a visitor's clock or
       time zone cannot change the countdown. Only the offset between the two clocks is kept here. */
    var serverClose = parseInt(cfg.dropClose, 10) || 0;
    var clockOffset = (parseInt(cfg.serverNow, 10) || 0) ? (parseInt(cfg.serverNow, 10) * 1000 - Date.now()) : 0;
    function dropCloses() {
      if (serverClose > 0) return new Date(serverClose * 1000 - clockOffset);
      var now = new Date();
      var c = new Date(now.getFullYear(), now.getMonth(), closeDay, 23, 59, 59);
      if (now > c) c = new Date(now.getFullYear(), now.getMonth() + 1, closeDay, 23, 59, 59);
      return c;
    }
    function tick() {
      var ms = dropCloses() - Date.now();
      var d = Math.floor(ms / 864e5);
      var h = Math.floor(ms % 864e5 / 36e5);
      var m = Math.floor(ms % 36e5 / 6e4);
      var s = Math.floor(ms % 6e4 / 1e3);
      var txt = ms <= 0 ? 'SOON' : d > 0 ? d + 'd ' + pad(h) + 'h' : pad(h) + ':' + pad(m) + ':' + pad(s);
      dropEls.forEach(function (el) { el.textContent = txt; });

      if (qteEnds) {
        var q = Math.max(0, qteEnds - Date.now());
        qteEls.forEach(function (el) {
          el.textContent = pad(Math.floor(q / 36e5)) + ':' + pad(Math.floor(q % 36e5 / 6e4)) + ':' + pad(Math.floor(q % 6e4 / 1e3));
        });
      }
    }
    tick();
    var timer = setInterval(tick, 1000);
    document.addEventListener('visibilitychange', function () {
      clearInterval(timer);
      if (!document.hidden) { tick(); timer = setInterval(tick, 1000); }
    });
  })();

  /* ══════════════════════════════════════════════════════════════════
     7. HUD ROTATION
     ══════════════════════════════════════════════════════════════════ */
  (function hud() {
    var el = $('[data-hud-msg]');
    if (!el || reduced) return;
    /* Do not rotate over "back Monday at 09:00". That is status, not copy. */
    if (el.textContent.indexOf('back') === 0) return;
    var msgs = (cfg.hud || '').split('|').map(function (m) { return m.trim(); }).filter(Boolean);
    if (msgs.length < 2) return;
    var i = 0;
    setInterval(function () {
      if (document.hidden) return;
      i = (i + 1) % msgs.length;
      el.style.opacity = '0';
      setTimeout(function () { el.textContent = msgs[i]; el.style.opacity = '1'; }, 300);
    }, 6000);
  })();

  /* ══════════════════════════════════════════════════════════════════
     8. SEARCH SHORTCUT
     ══════════════════════════════════════════════════════════════════ */
  document.addEventListener('keydown', function (e) {
    if (e.key !== '/' || e.metaKey || e.ctrlKey) return;
    var a = document.activeElement;
    var tag = a && a.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || (a && a.isContentEditable)) return;
    var box = $('.bh-search input');
    if (!box) return;
    e.preventDefault();
    box.focus();
  });

  /* ══════════════════════════════════════════════════════════════════
     9. PREDICTIVE SEARCH
     Talks to Shopify's own suggest endpoint rather than to the host theme's
     search component. That keeps the section self-contained: no dependency
     on a class name or custom element in Impact that a theme update could
     rename out from under it.
     ══════════════════════════════════════════════════════════════════ */
  (function predictiveSearch() {
    var wraps = [].slice.call(document.querySelectorAll('[data-bh-suggest]'));
    if (!wraps.length || !window.fetch) return;

    var base = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/$/, '');
    var searchUrl = base + '/search';
    var LIMIT = 6;
    var FETCH = 10;
    var MIN = 2;

    /* Prebuilt PCs rank above parts. A prebuilt is a product whose type is
       one of the build types: the shop's own list from the header settings
       (the same list the free delivery rule reads) plus the three ranges, so
       a saved setting that leaves one out does not demote it. Singular and
       plural are treated alike, so "Workstation" and "Workstations" match.
       Parts and components have no type, which is what keeps them below. */
    function typeKey(t) {
      return String(t || '').toLowerCase().replace(/\s+/g, ' ').trim().replace(/s$/, '');
    }
    var BUILD_TYPES = [];
    String(cfg.buildTypes || '').split(',').concat(['Gaming PC', 'Workstation', 'Office PC', 'Prebuilt']).forEach(function (t) {
      var k = typeKey(t);
      if (k && BUILD_TYPES.indexOf(k) === -1) BUILD_TYPES.push(k);
    });
    function isBuild(p) {
      var k = typeKey(p.type || p.product_type);
      return !!k && BUILD_TYPES.indexOf(k) > -1;
    }

    function esc(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
    function money(v) {
      var n = parseFloat(v);
      if (isNaN(n)) return '';
      try {
        return new Intl.NumberFormat('en-ZA', {
          style: 'currency', currency: cfg.currency || 'ZAR', maximumFractionDigits: 0
        }).format(n);
      } catch (e) {
        return 'R' + Math.round(n);
      }
    }
    function thumb(p) {
      var src = p.image || (p.featured_image && p.featured_image.url) || '';
      if (!src) return '<span class="bh-sug-shot" aria-hidden="true"></span>';
      /* Ask Shopify for a small one rather than shrinking a 2000px hero. */
      var sized = src.indexOf('?') === -1 ? src + '?width=144' : src + '&width=144';
      return '<span class="bh-sug-shot"><img src="' + esc(sized) + '" alt="" width="48" height="48" loading="lazy"></span>';
    }

    wraps.forEach(function (wrap) {
      var input = wrap.querySelector('[data-bh-suggest-input]');
      var panel = wrap.querySelector('[data-bh-suggest-panel]');
      var spin = wrap.querySelector('[data-bh-spin]');
      var form = wrap.querySelector('form');
      if (!input || !panel) return;

      var inHeader = !panel.classList.contains('bh-sug--drawer');
      var timer = null;
      var controller = null;
      var options = [];
      var active = -1;
      var lastQuery = '';

      function setBusy(on) {
        if (spin) spin.dataset.on = on ? 'true' : 'false';
      }

      function close() {
        panel.hidden = true;
        wrap.dataset.open = 'false';
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        options = [];
        active = -1;
        if (inHeader && suggestOpen) {
          suggestOpen = false;
          updateLift();
        }
      }

      function open() {
        panel.hidden = false;
        wrap.dataset.open = 'true';
        input.setAttribute('aria-expanded', 'true');
        options = [].slice.call(panel.querySelectorAll('[role="option"]'));
        active = -1;
        if (inHeader && !suggestOpen) {
          suggestOpen = true;
          updateLift();
        }
        if (inHeader) verifyOnTop(panel);
      }

      function highlight(i) {
        if (!options.length) return;
        if (active > -1 && options[active]) {
          options[active].removeAttribute('aria-selected');
          delete options[active].dataset.active;
        }
        active = i;
        if (active < 0) {
          input.removeAttribute('aria-activedescendant');
          return;
        }
        var el = options[active];
        el.setAttribute('aria-selected', 'true');
        el.dataset.active = 'true';
        input.setAttribute('aria-activedescendant', el.id);
        if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
      }

      function render(data, q) {
        var results = (data && data.resources && data.resources.results) || {};
        var products = results.products || [];
        var collections = results.collections || [];
        var html = '';
        var n = 0;
        var uid = panel.id + '-o';

        if (products.length) {
          var builds = products.filter(isBuild);
          var parts = products.filter(function (p) { return !isBuild(p); });
          var groups;
          if (builds.length && parts.length) {
            /* Always leave room for one part, so a search for a GPU still
               shows the card itself under the PCs that carry it. */
            var nb = Math.min(builds.length, LIMIT - 1);
            groups = [
              ['Prebuilt PCs', builds.slice(0, nb)],
              ['Parts and components', parts.slice(0, LIMIT - nb)]
            ];
          } else {
            groups = [[builds.length ? 'Prebuilt PCs' : 'Parts and gear', products.slice(0, LIMIT)]];
          }
          groups.forEach(function (g) {
            html += '<p class="bh-sug-lbl">' + g[0] + '</p>';
            g[1].forEach(function (p) {
              var price = money(p.price);
              html += '<a class="bh-sug-item" role="option" id="' + uid + n + '" href="' + esc(p.url) + '">' +
                thumb(p) +
                '<span class="bh-sug-b"><span class="bh-sug-t">' + esc(p.title) + '</span>' +
                ((p.product_type || p.type) ? '<span class="bh-sug-m">' + esc(p.product_type || p.type) + '</span>' : '') +
                '</span>' +
                (price ? '<span class="bh-sug-p">' + esc(price) + '</span>' : '') +
                '</a>';
              n++;
            });
          });
        }

        if (collections.length) {
          html += '<p class="bh-sug-lbl">Collections</p>';
          collections.forEach(function (c) {
            html += '<a class="bh-sug-item bh-sug-item--slim" role="option" id="' + uid + n + '" href="' + esc(c.url) + '">' +
              '<span class="bh-sug-b"><span class="bh-sug-t">' + esc(c.title) + '</span></span></a>';
            n++;
          });
        }

        if (!n) {
          html += '<p class="bh-sug-empty">Nothing matched that yet. Try a GPU, a game or a range name.</p>';
        }

        /* Always offer the full results page, so a search is never a dead
           end just because the top six did not have it. */
        html += '<a class="bh-sug-all" role="option" id="' + uid + n + '" href="' + esc(searchUrl) + '?q=' + encodeURIComponent(q) + '">' +
          'See all results for &ldquo;' + esc(q) + '&rdquo; <em>&rarr;</em></a>';

        panel.innerHTML = html;
        open();
      }

      function query(q) {
        if (controller) controller.abort();
        controller = ('AbortController' in window) ? new AbortController() : null;
        setBusy(true);
        var url = base + '/search/suggest.json?q=' + encodeURIComponent(q) +
          '&resources[type]=product,collection' +
          '&resources[limit]=' + FETCH +
          '&resources[options][unavailable_products]=last' +
          '&resources[options][fields]=title,product_type,variants.title,vendor,tag';
        fetch(url, controller ? { signal: controller.signal } : undefined)
          .then(function (r) { return r.json(); })
          .then(function (data) {
            setBusy(false);
            /* A slow response for an old query must not overwrite a newer one. */
            if (q !== lastQuery) return;
            render(data, q);
          })
          .catch(function (err) {
            if (err && err.name === 'AbortError') return;
            setBusy(false);
            close();
          });
      }

      input.addEventListener('input', function () {
        var q = input.value.trim();
        lastQuery = q;
        if (timer) clearTimeout(timer);
        if (q.length < MIN) {
          if (controller) controller.abort();
          setBusy(false);
          close();
          return;
        }
        timer = setTimeout(function () { query(q); }, 180);
      });

      input.addEventListener('focus', function () {
        if (panel.innerHTML && input.value.trim().length >= MIN) open();
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          close();
          return;
        }
        if (!options.length || panel.hidden) return;
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          highlight(active + 1 >= options.length ? 0 : active + 1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          highlight(active - 1 < 0 ? options.length - 1 : active - 1);
        } else if (e.key === 'Enter' && active > -1) {
          e.preventDefault();
          var target = options[active].href;
          close();
          releaseLift();
          location.href = target;
        } else if (e.key === 'Tab') {
          close();
        }
      });

      if (form) {
        form.addEventListener('submit', function () { close(); });
      }

      /* Close when focus or the pointer leaves the whole control, but not
         while moving from the input onto a suggestion. */
      wrap.addEventListener('focusout', function () {
        setTimeout(function () {
          if (!wrap.contains(document.activeElement)) close();
        }, 0);
      });
      document.addEventListener('pointerdown', function (e) {
        if (panel.hidden) return;
        if (!wrap.contains(e.target)) close();
      }, true);
    });
  })();

  /* ══════════════════════════════════════════════════════════════════
     10. OVERLAY PLUMBING
     Shared by the account panel and the cart panel: scroll lock, focus
     trap, Escape, and returning focus where it came from.
     ══════════════════════════════════════════════════════════════════ */
  var scrollLocks = 0;

  function lockScroll(on) {
    scrollLocks = Math.max(0, scrollLocks + (on ? 1 : -1));
    document.documentElement.style.overflow = scrollLocks ? 'hidden' : '';
  }

  function trapTab(container) {
    return function (e) {
      if (e.key !== 'Tab') return;
      var f = $$('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', container)
        .filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
  }

  /* ══════════════════════════════════════════════════════════════════
     11. ACCOUNT PANEL
     The icon is still a real link to the account page, so it works with the
     script off and on a modified click. The panel only intercepts a plain
     left click.
     ══════════════════════════════════════════════════════════════════ */
  (function accountPop() {
    var wrap = $('[data-bh-pop]');
    if (!wrap) return;
    var toggle = wrap.querySelector('[data-bh-pop-toggle]');
    var panel = wrap.querySelector('[data-bh-pop-panel]');
    if (!toggle || !panel) return;

    function open() {
      panel.hidden = false;
      wrap.dataset.open = 'true';
      toggle.setAttribute('aria-expanded', 'true');
      suggestOpen = true;
      updateLift();
      verifyOnTop(panel);
      var first = panel.querySelector('a[href]');
      if (first) first.focus({ preventScroll: true });
    }
    function close(refocus) {
      if (panel.hidden) return;
      panel.hidden = true;
      wrap.dataset.open = 'false';
      toggle.setAttribute('aria-expanded', 'false');
      suggestOpen = false;
      updateLift();
      if (refocus) toggle.focus();
    }

    toggle.addEventListener('click', function (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      if (panel.hidden) open();
      else close(true);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close(true);
    });
    document.addEventListener('pointerdown', function (e) {
      if (panel.hidden || wrap.contains(e.target)) return;
      close(false);
    }, true);
    wrap.addEventListener('focusout', function () {
      setTimeout(function () {
        if (!wrap.contains(document.activeElement)) close(false);
      }, 0);
    });
  })();

  /* ══════════════════════════════════════════════════════════════════
     12. CART PANEL
     Built here rather than driven through the host theme's drawer. Reaching
     into another theme's component means guessing at a class name or a
     method that a theme update can rename, and a cart button that silently
     does nothing is worse than one that navigates. This talks to Shopify's
     own /cart.js endpoints instead, so it behaves the same whatever the
     theme is doing.
     ══════════════════════════════════════════════════════════════════ */
  (function cartPanel() {
    var panel = document.querySelector('[data-bh-cart]');
    var scrim = document.querySelector('[data-bh-cart-scrim]');
    var inv = shell.querySelector('[data-bh-inv][data-bh-cart-drawer="true"]');
    if (!panel || !scrim || !inv) return;

    var body = panel.querySelector('[data-cart-body]');
    var foot = panel.querySelector('[data-cart-foot]');
    var base = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/$/, '');
    var buildTypes = (cfg.buildTypes || '').split(',')
      .map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean);
    var lastFocus = null;
    var busy = false;

    function esc(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
    function money(cents) {
      try {
        return new Intl.NumberFormat('en-ZA', {
          style: 'currency', currency: cfg.currency || 'ZAR', maximumFractionDigits: 0
        }).format(cents / 100);
      } catch (e) {
        return 'R' + Math.round(cents / 100);
      }
    }

    function render(cart) {
      var items = cart.items || [];
      panel.querySelector('[data-cart-n]').textContent = cart.item_count || 0;

      if (!items.length) {
        body.innerHTML =
          '<div class="bh-cart-empty">' +
          '<p class="bh-cart-empty-t">Nothing in inventory yet</p>' +
          '<p class="bh-cart-empty-s">Pick a range and a tier, or start from a blank build.</p>' +
          '</div>';
        foot.hidden = true;
        return;
      }

      var html = '';
      items.forEach(function (line, i) {
        var img = line.image ? line.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') : '';
        var variant = (line.variant_title && line.variant_title !== 'Default Title') ? line.variant_title : '';
        html +=
          '<div class="bh-cart-line" data-line="' + (i + 1) + '">' +
            '<a class="bh-cart-shot" href="' + esc(line.url) + '">' +
              (img ? '<img src="' + esc(img) + '" alt="" width="60" height="60" loading="lazy">' : '') +
            '</a>' +
            '<div class="bh-cart-info">' +
              '<a class="bh-cart-name" href="' + esc(line.url) + '">' + esc(line.product_title || line.title) + '</a>' +
              (variant ? '<span class="bh-cart-var">' + esc(variant) + '</span>' : '') +
              '<div class="bh-cart-row">' +
                '<div class="bh-qty">' +
                  '<button type="button" data-qty="-1" aria-label="Decrease quantity">&minus;</button>' +
                  '<b>' + line.quantity + '</b>' +
                  '<button type="button" data-qty="1" aria-label="Increase quantity">+</button>' +
                '</div>' +
                '<span class="bh-cart-price">' + esc(money(line.final_line_price)) + '</span>' +
              '</div>' +
              '<button type="button" class="bh-cart-rm" data-remove aria-label="Remove ' + esc(line.product_title || line.title) + '">Remove</button>' +
            '</div>' +
          '</div>';
      });
      body.innerHTML = html;

      var hasBuild = items.some(function (line) {
        var type = (line.product_type || '').toLowerCase();
        return buildTypes.some(function (t) { return t && type.indexOf(t) !== -1; });
      });
      var free = panel.querySelector('[data-cart-free]');
      free.textContent = hasBuild ? 'Free delivery included' : 'Delivery worked out at checkout';
      free.dataset.on = String(hasBuild);
      panel.querySelector('[data-cart-total]').textContent = money(cart.total_price || 0);
      foot.hidden = false;
    }

    function load() {
      body.setAttribute('aria-busy', 'true');
      return fetch(base + '/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { body.setAttribute('aria-busy', 'false'); render(cart); return cart; })
        .catch(function () {
          body.setAttribute('aria-busy', 'false');
          body.innerHTML = '<div class="bh-cart-empty"><p class="bh-cart-empty-t">Could not load your cart</p>' +
            '<p class="bh-cart-empty-s"><a href="' + esc(base + '/cart') + '">Open the cart page instead</a></p></div>';
          foot.hidden = true;
        });
    }

    function change(line, quantity) {
      if (busy) return;
      busy = true;
      panel.dataset.busy = 'true';
      fetch(base + '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          render(cart);
          /* Tell the rest of the page, including the Inventory pill and
             whatever the theme is listening for. */
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: cart }));
        })
        .catch(function () { load(); })
        .then(function () {
          busy = false;
          delete panel.dataset.busy;
        });
    }

    body.addEventListener('click', function (e) {
      var lineEl = e.target.closest('[data-line]');
      if (!lineEl) return;
      var line = parseInt(lineEl.dataset.line, 10);
      var qtyBtn = e.target.closest('[data-qty]');
      if (qtyBtn) {
        var now = parseInt(lineEl.querySelector('.bh-qty b').textContent, 10) || 0;
        change(line, Math.max(0, now + parseInt(qtyBtn.dataset.qty, 10)));
        return;
      }
      if (e.target.closest('[data-remove]')) change(line, 0);
    });

    var trap = trapTab(panel);

    function open() {
      lastFocus = document.activeElement;
      panel.hidden = false;
      scrim.hidden = false;
      requestAnimationFrame(function () {
        panel.dataset.on = 'true';
        scrim.dataset.on = 'true';
      });
      lockScroll(true);
      setOverlay(true, panel);
      inv.setAttribute('aria-expanded', 'true');
      panel.addEventListener('keydown', trap);
      load();
      setTimeout(function () {
        var close = panel.querySelector('[data-bh-cart-close]');
        if (close) close.focus({ preventScroll: true });
      }, 260);
    }

    function close() {
      if (panel.hidden) return;
      panel.dataset.on = 'false';
      scrim.dataset.on = 'false';
      lockScroll(false);
      setOverlay(false, panel);
      inv.setAttribute('aria-expanded', 'false');
      panel.removeEventListener('keydown', trap);
      setTimeout(function () { panel.hidden = true; scrim.hidden = true; }, 300);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    inv.addEventListener('click', function (e) {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      open();
    });
    scrim.addEventListener('click', close);
    panel.querySelectorAll('[data-bh-cart-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) close();
    });
  })();

  /* ══════════════════════════════════════════════════════════════════
     13. DRAWER
     A bottom sheet with a grip that actually works. The one this replaces
     drew the universal "swipe me down" handle with no touch listener
     anywhere behind it.
     ══════════════════════════════════════════════════════════════════ */
  (function drawer() {
    var panel = document.querySelector('[data-bh-drawer]');
    var scrim = document.querySelector('[data-bh-scrim]');
    if (!panel || !scrim) return;
    var opener = $('[data-bh-open-drawer]');
    var grip = panel.querySelector('[data-bh-grip]');
    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      panel.hidden = false;
      scrim.hidden = false;
      requestAnimationFrame(function () {
        panel.dataset.on = 'true';
        scrim.dataset.on = 'true';
      });
      lockScroll(true);
      setOverlay(true, panel);
      if (opener) opener.setAttribute('aria-expanded', 'true');
      setTimeout(function () {
        var box = panel.querySelector('.bh-dr-search input');
        if (box) box.focus({ preventScroll: true });
      }, 320);
    }

    function close() {
      panel.dataset.on = 'false';
      scrim.dataset.on = 'false';
      lockScroll(false);
      setOverlay(false, panel);
      if (opener) opener.setAttribute('aria-expanded', 'false');
      setTimeout(function () {
        panel.hidden = true;
        scrim.hidden = true;
        panel.style.transform = '';
      }, 300);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    if (opener) opener.addEventListener('click', open);
    scrim.addEventListener('click', close);
    $$('[data-bh-close-drawer]', panel).forEach(function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) close();
    });

    /* Focus trap */
    panel.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var f = $$('a[href], button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"])', panel)
        .filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    /* Drag to dismiss */
    if (!grip) return;
    var startY = 0, dy = 0, dragging = false;

    function pointY(e) { return e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY; }
    function down(e) {
      dragging = true; dy = 0;
      startY = pointY(e);
      panel.dataset.dragging = 'true';
    }
    function move(e) {
      if (!dragging) return;
      dy = Math.max(0, pointY(e) - startY);
      if (e.cancelable) e.preventDefault();
      panel.style.transform = 'translateY(' + dy + 'px)';
    }
    function up() {
      if (!dragging) return;
      dragging = false;
      delete panel.dataset.dragging;
      if (dy > 90) close();
      else panel.style.transform = '';
    }

    grip.addEventListener('touchstart', down, { passive: true });
    grip.addEventListener('touchmove', move, { passive: false });
    grip.addEventListener('touchend', up);
    grip.addEventListener('mousedown', function (e) { e.preventDefault(); down(e); });
    addEventListener('mousemove', move);
    addEventListener('mouseup', up);
    grip.addEventListener('click', function () { if (dy === 0) close(); });
  })();

  /* ══════════════════════════════════════════════════════════════════
     14. PHONE SEARCH SHEET
     The magnifier below 700px used to be a plain link to an empty search
     page, so search on a phone meant a page load, then typing into a form
     with no suggestions. It opens a full screen sheet instead. The link
     still works with the script off and on a modified click.

     Predictive results come from block 9 above: the sheet's input and panel
     carry the same data attributes, so there is one engine, not two.
     This block owns only what is specific to the sheet: open and close,
     recent searches, and sizing to the visual viewport so the keyboard
     shrinks the sheet rather than covering the results.
     ══════════════════════════════════════════════════════════════════ */
  (function searchSheet() {
    var sheet = document.querySelector('[data-bh-ss]');
    var openers = $$('[data-bh-open-search]');
    if (!sheet || !openers.length) return;

    var input = sheet.querySelector('[data-bh-ss-input]');
    var form = sheet.querySelector('form');
    var empty = sheet.querySelector('[data-bh-ss-empty]');
    var clear = sheet.querySelector('[data-bh-ss-clear]');
    var recentBox = sheet.querySelector('[data-bh-ss-recent]');
    var recentList = sheet.querySelector('[data-bh-ss-recent-list]');
    var forget = sheet.querySelector('[data-bh-ss-forget]');
    if (!input || !form || !empty || !clear) return;

    var wide = matchMedia('(min-width: 700px)');
    var vv = window.visualViewport;
    var trap = trapTab(sheet);
    var searchUrl = form.getAttribute('action') || '/search';
    var KEY = 'bh-recent-searches';
    var MAX = 5;
    var isOpen = false;
    var lastFocus = null;
    var hideTimer = 0;

    /* Recent searches stay on this device. Storage can throw (private
       windows, blocked site data), so every touch of it is guarded and the
       sheet works the same without it. */
    function readRecent() {
      try {
        var v = JSON.parse(localStorage.getItem(KEY) || '[]');
        return Array.isArray(v)
          ? v.filter(function (q) { return typeof q === 'string' && q; }).slice(0, MAX)
          : [];
      } catch (e) { return []; }
    }
    function writeRecent(list) {
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) { /* ignore */ }
    }
    function remember(q) {
      q = String(q || '').replace(/\s+/g, ' ').trim().slice(0, 60);
      if (q.length < 2) return;
      var list = readRecent().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
      list.unshift(q);
      writeRecent(list.slice(0, MAX));
    }
    function paintRecent() {
      if (!recentBox || !recentList) return;
      var list = readRecent();
      recentList.textContent = '';
      list.forEach(function (q) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.className = 'bh-ss__chip';
        a.href = searchUrl + '?q=' + encodeURIComponent(q);
        a.textContent = q;
        li.appendChild(a);
        recentList.appendChild(li);
      });
      recentBox.hidden = !list.length;
    }

    /* Two states: nothing typed shows shortcuts, two or more characters
       hands the screen to the suggestions. */
    function sync() {
      clear.hidden = !input.value;
      empty.hidden = input.value.trim().length >= 2;
    }

    /* Track the visual viewport. On iOS the keyboard shrinks it without
       resizing the layout viewport, so a fixed sheet would run under it. */
    function fit() {
      if (!vv) return;
      sheet.style.setProperty('--bh-ss-h', Math.round(vv.height) + 'px');
      sheet.style.setProperty('--bh-ss-top', Math.round(vv.offsetTop) + 'px');
    }

    function setExpanded(on) {
      openers.forEach(function (el) { el.setAttribute('aria-expanded', on ? 'true' : 'false'); });
    }

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocus = document.activeElement;
      window.clearTimeout(hideTimer);
      paintRecent();
      sync();
      sheet.hidden = false;
      fit();
      requestAnimationFrame(function () { sheet.dataset.on = 'true'; });
      lockScroll(true);
      setOverlay(true, sheet);
      setExpanded(true);
      sheet.addEventListener('keydown', trap);
      if (vv) {
        vv.addEventListener('resize', fit);
        vv.addEventListener('scroll', fit);
      }
      /* Focus inside the tap itself. iOS only raises the keyboard for a
         focus() that happens in the gesture, not one a timer fires later. */
      try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
      if (input.value) input.select();
    }

    function close(refocus, instant) {
      if (!isOpen) return;
      isOpen = false;
      sheet.dataset.on = 'false';
      lockScroll(false);
      setOverlay(false, sheet);
      setExpanded(false);
      sheet.removeEventListener('keydown', trap);
      if (vv) {
        vv.removeEventListener('resize', fit);
        vv.removeEventListener('scroll', fit);
      }
      input.blur();
      function done() { if (!isOpen) sheet.hidden = true; }
      if (reduced || instant) done();
      else hideTimer = window.setTimeout(done, 230);
      if (refocus !== false && lastFocus && lastFocus.focus) {
        try { lastFocus.focus({ preventScroll: true }); } catch (e) { lastFocus.focus(); }
      }
    }

    openers.forEach(function (el) {
      el.addEventListener('click', function (e) {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (wide.matches) return;
        e.preventDefault();
        open();
      });
    });

    sheet.querySelector('[data-bh-ss-close]').addEventListener('click', function () { close(true); });
    input.addEventListener('input', sync);
    clear.addEventListener('click', function () {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
    });
    if (forget) {
      forget.addEventListener('click', function () { writeRecent([]); paintRecent(); input.focus(); });
    }

    /* Searching, or following a suggestion, is worth remembering */
    form.addEventListener('submit', function () { remember(input.value); });
    sheet.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('.bh-sug-item, .bh-sug-all');
      if (link) remember(input.value);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) close(true);
    });

    /* Rotating a phone past 700px brings the header's own search bar back */
    function onWide() { if (wide.matches) close(false, true); }
    if (wide.addEventListener) wide.addEventListener('change', onWide);
    else if (wide.addListener) wide.addListener(onWide);

    /* Back from a result can restore this page from the browser's cache with
       the sheet still open and the page still locked */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted && isOpen) close(false, true);
    });
  })();
})();
